import { MedplumClient } from "@medplum/core";
import { Encounter } from "@medplum/fhirtypes";

import { fetchEncounterProviderName } from "@/encounters/services/encounter";
import {
  findEncounterParticipantByType,
  resolveEncounterSignedAndLockedByReference,
} from "@/encounters/utils/encounter-participants";
import { EncounterPractitionerType } from "@/fhir/enums/quilted-health";
import { getDisplayStringAsync } from "@/utils/medplum";

/**
 * Provider label for a Missing Services row. The signing practitioner is authoritative and is read
 * off the `EncounterSignedAndLockedBy` `meta.tag` — the same value the queue's Provider filter
 * searches by, so column and filter always agree.
 *
 * Encounters written before the tag existed fall back to participants: the `signed-and-locked-by`
 * participant, then the clinical `provider` participant. `meta.author` is deliberately never used —
 * it names whoever last wrote the resource, not the provider of the encounter.
 *
 * @param medplum Medplum client used to resolve a reference without a display into a name
 * @param encounter Encounter backing the row
 * @returns Provider display name, or an empty string when none can be resolved
 */
export async function resolveMissingServicesProviderDisplay(
  medplum: MedplumClient,
  encounter: Encounter,
): Promise<string> {
  const taggedReference = resolveEncounterSignedAndLockedByReference(encounter);
  if (taggedReference) {
    const fromTag = await getDisplayStringAsync(medplum, taggedReference);
    const trimmedTagName = fromTag?.trim();
    if (trimmedTagName) return trimmedTagName;
  }

  const signerParticipant = findEncounterParticipantByType(
    encounter,
    EncounterPractitionerType.SignedAndLockedBy,
  );
  const fromSigner = await getDisplayStringAsync(medplum, signerParticipant?.individual);
  const trimmedSignerName = fromSigner?.trim();
  if (trimmedSignerName) return trimmedSignerName;

  const fromProviderParticipant = await fetchEncounterProviderName(medplum, encounter.participant);
  return fromProviderParticipant?.trim() ?? "";
}
